import React from "react";
import { TextField } from "./fields/TextField";
import { EmailField } from "./fields/EmailField";
import { DateField } from "./fields/DateField";
import { TimeField } from "./fields/TimeField";
import { SelectField } from "./fields/SelectField";
import { AsyncSearchableSelectField } from "./fields/AsyncSearchableSelectField";
import { NumberField } from "./fields/NumberField";
import { TextAreaField } from "./fields/TextAreaField";
import { BooleanField } from "./fields/BooleanField";
import { BooleanToggleField } from "./fields/BooleanToggleField";
import { ExpandableTextAreaField } from "./fields/ExpandableTextAreaField";
import { MultiSelectField } from "./fields/MultiSelectField";
import { AddressField } from "./fields/AddressField";
import { EmployeeField } from "./fields/EmployeeField";
import { HabitSelectionField } from "./fields/HabitSelectionField";
import { ScoreSliderField } from "./fields/ScoreSliderField";
import { AvatarPreviewField } from "./fields/AvatarPreviewField";
import { ImageUploadField } from "./fields/ImageUploadField";
import { AvatarUploadField } from "./fields/AvatarUploadField";
import { MoneyField } from "./fields/MoneyField";
import { PercentageSlider } from "./fields/PercentageSlider";
import { BadgesField } from "./fields/BadgesField";
import { ChildLabelsField } from "./fields/ChildLabelsField";
import { GroupTypesField } from "./fields/GroupTypesField";
import { PhoneField } from "./fields/PhoneField";
import { TriStateField } from './fields/TriStateField';
import { ChecklistField } from './fields/ChecklistField';
import { LanguageProficiencyField } from "./fields/LanguageProficiencyField";
import type { FieldConfig } from "./types";

// Common props that all field components receive
export interface FieldComponentProps<T> {
  field: FieldConfig<T>;
  value: T;
  onChange: (value: T) => void;
  onBlur: () => void;  // Made required to match field implementations
  disabled?: boolean;
  id: string;
  placeholder?: string;
  "aria-describedby"?: string;
  /** Set by BaseEditableForm while the field has an error. */
  "aria-invalid"?: boolean;
}

// Type for field components
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FieldComponent<T = any> = React.ComponentType<FieldComponentProps<T>>;

// Field registry mapping field types to their components
export const FIELD_COMPONENTS: Record<string, FieldComponent> = {
  text: TextField,
  email: EmailField,
  textarea: TextAreaField,
  expandabletextarea: ExpandableTextAreaField,
  number: NumberField,
  date: DateField,
  time: TimeField, // wall-clock HH:mm string, no timezone — see TimeField
  select: SelectField,
  asyncselect: AsyncSearchableSelectField, // server-side search dropdown — see field.loadOptions
  multiselect: MultiSelectField,
  boolean: BooleanField,
  booleantoggle: BooleanToggleField,
  reference: SelectField, // reference uses the same component as select
  address: AddressField, // custom address selector field
  employee: EmployeeField, // shared employee picker (issue #1217) — see field.excludeEntityId
  habitselection: HabitSelectionField, // habit selection with three-state toggles
  scoreslider: ScoreSliderField, // score slider for 0-100 ratings
  avatarpreview: AvatarPreviewField, // avatar URL input with live preview
  imageupload: ImageUploadField, // image upload with drag-drop and progress
  avatarupload: AvatarUploadField, // specialized avatar upload with preview
  money: MoneyField, // money input field with CHF formatting
  percentage: PercentageSlider, // percentage slider with dual controls
  badges: BadgesField, // read-only badges display for arrays
  childlabels: ChildLabelsField, // specialized child labels selection with management
  grouptypes: GroupTypesField, // group types display with edit link for billing settings
  phone: PhoneField, // phone input with country code dropdown
  languageproficiency: LanguageProficiencyField, // language with proficiency level selector
  tristate: TriStateField, // settling-in indicator tri-state (unset/good/neutral/bad) per option
  checklist: ChecklistField, // compact boolean checklist rows with labelled state
} as const;

// Default fallback field component
export const DEFAULT_FIELD_COMPONENT = TextField;

/**
 * Get the appropriate field component for a given field type
 * @param fieldType - The type of field to get the component for
 * @returns The React component for rendering that field type
 */
export function getFieldComponent(fieldType: string): FieldComponent {
  return FIELD_COMPONENTS[fieldType] || DEFAULT_FIELD_COMPONENT;
}

/**
 * Check if a field type is supported by the registry
 * @param fieldType - The field type to check
 * @returns True if the field type is supported
 */
export function isFieldTypeSupported(fieldType: string): boolean {
  return fieldType in FIELD_COMPONENTS;
}

/**
 * Get all supported field types
 * @returns Array of all supported field type strings
 */
export function getSupportedFieldTypes(): string[] {
  return Object.keys(FIELD_COMPONENTS);
} 